import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Textarea,
  Separator,
  toast,
} from "@mindstonehq/ui";
import React from "react";
import Blockquote, { HighlightBlockquote } from "./blockquote";
import { useMutation } from "@apollo/client/react";
import type {
  FullResourceFragment,
  ResourceCommentFragment,
  UpdatePrivateNoteMutation,
  UpdatePrivateNoteMutationVariables,
  CreateAnnotationHighlightMutation,
  CreateAnnotationHighlightMutationVariables,
  PdfHighlightInput,
} from "~/graphql/codegen/graphql";
import {
  UPDATE_PRIVATE_NOTE_MUTATION,
  CREATE_ANNOTATION_HIGHLIGHT_MUTATION,
} from "../annotate-mutations";
import useAuthCheck from "~/hooks/check-authenticated";
import metrics from "~/utils/metrics";
import {
  IHighlightCommon,
  IHtmlHighlight,
  IHtmlHighlightInput,
  IPdfHighlight,
  IPdfHighlightInput,
} from "~/typings/annotate/annotation";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function DialogHighlightNote({
  article,
  close,
  annotations,
  selectedHighlight,
  setSelectedHighlight,
  refresh,
  startingText,
}: {
  article: FullResourceFragment;
  close: () => void;
  annotations: {
    comments: ResourceCommentFragment[];
    highlights: (IHtmlHighlight | IPdfHighlight)[];
  };
  selectedHighlight:
    | IHtmlHighlightInput
    | IPdfHighlightInput
    | IHighlightCommon
    | null
    | undefined;
  setSelectedHighlight: (
    highlight:
      | IHtmlHighlightInput
      | IPdfHighlightInput
      | IHighlightCommon
      | null
      | undefined,
  ) => void;
  refresh: () => void;
  startingText?: string;
}) {
  const { t } = useLingui();
  const [open, setOpen] = React.useState(false);
  const [updatePrivateNote] = useMutation<UpdatePrivateNoteMutation, UpdatePrivateNoteMutationVariables>(UPDATE_PRIVATE_NOTE_MUTATION);
  const [createHighlightMutation] = useMutation<CreateAnnotationHighlightMutation, CreateAnnotationHighlightMutationVariables>(CREATE_ANNOTATION_HIGHLIGHT_MUTATION);
  const checkAuth = useAuthCheck();
  const [noteText, setNoteText] = React.useState(startingText || "");
  const timeoutRef = React.useRef<number | null>(null);
  const [lastUpdated, setLastUpdated] = React.useState<Date>();
  const allPrivateNotes = annotations.highlights.filter((h) => !!h.privateNote);

  React.useEffect(() => {
    // If existing note and not saving as note from askAI
    if (selectedHighlight && !startingText) {
      setNoteText(
        allPrivateNotes.find((h) => h.id === selectedHighlight.id)?.privateNote
          ?.content || "",
      );
    }
  }, []);

  function updateNote(content: string) {
    const authenticated = checkAuth(
      t(msg`You need to be logged in to add a note`),
      `${location.href}`,
      "private_note_edit",
    );
    if (!authenticated) {
      return Promise.resolve("");
    }
    const { comments, canEdit, profile, createdAt, ...inputFields } =
      selectedHighlight as IHighlightCommon;
    const highlight = inputFields as
      | IHtmlHighlightInput
      | IPdfHighlightInput
      | IHighlightCommon;
    metrics.track("private_note_create", {
      article_id: article.id,
      view: "annotate",
    });
    if (!highlight.id) {
      console.error("highlight ID is not defined");
      return Promise.reject("highlight ID is not defined");
    }
    let promise = Promise.resolve(highlight.id);
    let xfdfString = "";
    if (highlight.id === "temp") {
      // SAVE NEW HIGHLIGHT
      const { id, ...rest } = highlight;
      if (highlight.__typename === "PDFHighlight") {
        const h = rest as IPdfHighlightInput;
        xfdfString = h.xfdfString || "";
        promise = createHighlight(h);
      } else {
        const h = rest as IHtmlHighlightInput;
        promise = createHighlight(h);
      }
    }
    return promise.then((highlightID) => {
      return updatePrivateNote({
        variables: {
          articleID: article.id,
          highlightID: highlightID || "",
          content,
          xfdfString,
        },
      })
        .then((res) => {
          if (!res.data) {
            // eslint-disable-next-line lingui/no-unlocalized-strings -- dev-only thrown string
            throw "data missing";
          }
          setTimeout(() => {
            refresh();
          }, 1000);

          return (res.data.updatePrivateNote as IHighlightCommon).id;
        })
        .catch((error) => {
          console.error(error);
          toast.error(t(msg`We couldn't save this comment. Please try again later.`));
          throw error;
        });
    });
  }

  function createHighlight(h: IHtmlHighlightInput | IPdfHighlightInput) {
    return createHighlightMutation({
      variables: {
        articleId: article.id,
        htmlHighlightInput:
          h.__typename === "HTMLHighlight" ? (h as IHtmlHighlightInput) : null,
        pdfHighlightInput:
          h.__typename === "PDFHighlight" ? (h as PdfHighlightInput) : null,
      },
      onQueryUpdated: () => false,
    }).then((res) => {
      if (!res.data) {
        // eslint-disable-next-line lingui/no-unlocalized-strings -- dev-only thrown string
        throw "data missing";
      }
      setSelectedHighlight(
        res.data.createAnnotationHighlight as IHighlightCommon,
      );
      return res.data.createAnnotationHighlight.id;
    });
  }

  const handleNoteUpdate = (content: string) => {
    setNoteText(content);
    // No on the fly update for now
    // if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // timeoutRef.current = window.setTimeout(() => {
    //   saveNote(content);
    // }, 1000);
  };

  const saveNote = (content: string) => {
    updateNote(content).then(() => {
      setLastUpdated(new Date());
    });
  };

  const handleDone = () => {
    updateNote(noteText).then(close);
  };

  return (
    <DialogContent className="md:w-[550px]">
      <DialogHeader>
        <DialogTitle className="flex items-center flex-row space-x-2 mt-2">
          <Trans>Private Note</Trans>
          {lastUpdated && (
            <span className="text-placeholder-foreground ml-2">
              {" "}
              {lastUpdated.getHours()}:{lastUpdated.getMinutes()}
            </span>
          )}
        </DialogTitle>
        <Separator className="mt-[6px] bg-highlight" />
      </DialogHeader>
      <HighlightBlockquote
        text={selectedHighlight?.text || ""}
        model={selectedHighlight?.model}
      />
      <div className="flex flex-col flex-1 justify-between space-y-5 mt-6">
        <Textarea
          autoFocus
          className="flex-1"
          value={noteText}
          onChange={(e) => handleNoteUpdate(e.target.value)}
        />
        <Button
          variant="secondary"
          className="w-auto self-end"
          onClick={handleDone}
        >
          <Trans>Done</Trans>
        </Button>
      </div>
    </DialogContent>
  );
}
